import { apiFetch } from "./client";

export type DashPoint = { label: string; value: number };
export type DashStatusSlice = { status: string; orders: number };
export type DashCustomer = {
  companyId: string;
  name: string;
  country: string | null;
  amountEur: number;
};
export type DashDebtor = {
  companyId: string;
  name: string;
  country: string | null;
  overdueEur: number;
  oldestDays: number | null;
};

/**
 * Brojevi kontrolne table, iz dokumenata koje portal drži.
 *
 * Iznosi su preračunati u evre po kursu na dan dokumenta; broj porudžbina je broj, ne iznos, jer
 * porudžbina nema svoju valutu pa se preko kupaca ne može sabrati.
 */
export type SalesDashboard = {
  financeVisible?: boolean;
  ordersVisible?: boolean;
  invoicedYtd: number | null;
  /** Promena u odnosu na isti period lane; prazno kad lane nije bilo prometa. */
  invoicedYtdDeltaPct: number | null;
  /**
   * Opening balance carried from earlier years and posted since the start of the year. It is a
   * debit but not a sale, so it stays out of `invoicedYtd` and is named beside it instead.
   */
  carriedPostedEur: number | null;
  /** Other debits posted since the start of the year: reposts, rate differences, ledger orders. */
  otherPostedEur: number | null;
  ordersThisMonth: number | null;
  ordersDeltaPct: number | null;
  /**
   * Otvoreno i dospelo, zbir preko celog obima koji korisnik vidi.
   *
   * Nije zbir spiska `topDebtors` ispod: taj spisak je odsečen na nekoliko redova, pa bi zbir
   * preko njega bio manji od istine i ništa na ekranu to ne bi pokazalo.
   */
  overdueEur: number | null;
  /** Koliko kupaca nosi taj dug — broj uz iznos, da se vidi na šta se iznos odnosi. */
  overdueCompanies: number | null;
  /**
   * Tačno kad iznos važi samo za kupce ovog korisnika.
   *
   * Komercijalista vidi dodeljene kupce i svoja tržišta, direktor i administrator sve. Ekran to
   * mora da napiše, jer isti iznos bez te oznake izgleda kao ukupan dug firme.
   */
  overdueScopeLimited: boolean;
  /**
   * The day the outgoing invoices reach, or today if that comes first. The whole board is counted up
   * to it, the orders of this month and overdue included, so it comes with every grant.
   */
  dataUntil?: string | null;
  monthlyInvoiced: DashPoint[];
  pipeline: DashStatusSlice[];
  topCustomers: DashCustomer[];
  byCountry: DashPoint[];
  topDebtors: DashDebtor[];
};

export type DashSupplier = DashCustomer;
export type DashCreditor = DashDebtor;

/**
 * Brojevi kontrolne table otkupa — ista aritmetika kao na prodajnoj strani, druga strana knjige.
 *
 * Iznosi su u evrima, preračunati po kursu na dan dokumenta; broj prijema je broj, ne iznos, jer
 * `delivery.settled_amount` stoji prazno na svih 22.132 uvezena kamiona — ERP obračunava, portal
 * čita, i tu cifru nije dobio.
 */
export type ProcurementDashboard = {
  financeVisible?: boolean;
  intakeVisible?: boolean;
  /** Otkupljeno od početka godine, po ulaznim računima. */
  purchasedYtd: number | null;
  /** Promena u odnosu na isti period lane; prazno kad lane nije bilo prometa. */
  purchasedYtdDeltaPct: number | null;
  /**
   * Opening balance carried from earlier years and posted since the start of the year. It is owed
   * but not a purchase, so it stays out of `purchasedYtd` and is named beside it instead.
   */
  carriedPostedEur: number | null;
  /** Other postings since the start of the year: reposts, tax calculations, ledger orders. */
  otherPostedEur: number | null;
  /** Prijema ovog meseca — kamiona, čitano po danu prijema a ne po danu najave. */
  intakesThisMonth: number | null;
  intakesDeltaPct: number | null;
  /** Neto masa tih prijema, u kilogramima: količina iza broja. */
  intakeKgThisMonth: number | null;
  /**
   * Otvorena i dospela obaveza prema kooperantima, zbir preko celog obima koji korisnik vidi.
   *
   * Nije zbir spiska `topCreditors` ispod: taj spisak je odsečen na nekoliko redova, pa bi zbir
   * preko njega bio manji od istine i ništa na ekranu to ne bi pokazalo.
   */
  payableEur: number | null;
  /** Koliko kooperanata nosi tu obavezu — broj uz iznos, da se vidi na šta se iznos odnosi. */
  payableCompanies: number | null;
  /**
   * Tačno kad iznos važi samo za kooperante ovog korisnika.
   *
   * Referent otkupa po pravilu čita sve kooperante, pa je ovo netačno za njega i tačno za
   * čitaoca bez tog širokog prava. Ekran to mora da napiše, jer isti iznos bez te oznake
   * izgleda kao ukupna obaveza firme.
   */
  payableScopeLimited: boolean;
  /**
   * The day the incoming invoices reach, or today if that comes first. The money on the board is
   * counted up to it: this year and the same stretch of last year, the monthly chart and the
   * payable. Empty without the finance grant.
   */
  dataUntil?: string | null;
  /**
   * The day the intakes reach, or today if that comes first: this month's intakes are counted up to
   * it, against as many days of the month before. Empty without the intake grant.
   */
  intakesUntil?: string | null;
  monthlyPurchased: DashPoint[];
  intakePipeline: DashDeliverySlice[];
  topSuppliers: DashSupplier[];
  byCountry: DashPoint[];
  topCreditors: DashCreditor[];
};

export type DashDeliverySlice = { status: string; deliveries: number };

/** Jedna stavka reda koji čeka potpis šefa kvaliteta: nalaz na verifikaciji ili dokument uz LOT. */
export type DashSignatureItem = {
  id: string;
  kind: "READING" | "LOT_DOCUMENT";
  title: string;
  subtitle: string | null;
  since: string;
};

export type DashParameterGap = {
  parameterId: string;
  parameterKey: string;
  nameI18n: Record<string, string> | null;
  ordered: number;
  unmeasured: number;
};

export type DashKindSlice = { kind: string; analyses: number };

/**
 * Figures of the quality control panel.
 *
 * There is deliberately NO share „outside limits" here: the limits inherited from the lab program hang
 * off the article and the customer, not off a product specification, so a pass rate over them would
 * measure the limits, not the goods. In its place stands how many articles have no limit at all,
 * because that is what stands between the lab and working in the portal.
 */
export type QualityDashboard = {
  archiveVisible?: boolean;
  queueVisible?: boolean;
  analysesYtd: number | null;
  /** Promena u odnosu na isti period lane; prazno kad lane nije bilo analiza. */
  analysesYtdDeltaPct: number | null;
  /**
   * The day the archive figures are counted up to: the day of the last analysis in the archive, or
   * today if that comes first. Empty without the archive grant, or while the archive holds nothing.
   */
  archiveUntil: string | null;
  /** Testova naručenih ove godine, i koliko ih nikad nije izmereno. */
  testsOrdered: number | null;
  testsUnmeasured: number | null;
  /** Artikala koje je laboratorija merila, i koliko njih nema nijednu granicu. */
  articlesMeasured: number | null;
  articlesWithoutLimit: number | null;
  monthlyAnalyses: DashPoint[];
  byControlPointKind: DashKindSlice[];
  topControlPoints: DashPoint[];
  topArticles: DashPoint[];
  parameterGaps: DashParameterGap[];
  signatureQueue: DashSignatureItem[];
  /** Puna dužina reda, da odsečen spisak može da kaže da je odsečen. */
  signatureQueueSize: number;
};

export const dashboardApi = {
  sales: (months = 12) => apiFetch<SalesDashboard>(`/dashboard/sales?months=${months}`),
  procurement: (months = 12) =>
    apiFetch<ProcurementDashboard>(`/dashboard/procurement?months=${months}`),
  quality: (months = 12) => apiFetch<QualityDashboard>(`/dashboard/quality?months=${months}`),
};
